import React from 'react';
import { WheelSafeNumberInput } from '../../common/WheelSafeNumberInput';
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  DateField,
  FunctionField,
  Filter,
  TextInput,
  SelectInput,
  DateInput,
  useListContext,
  TopToolbar,
  ShowButton,
  useNotify,
  useRefresh,
  useTranslate,
  RecordContextProvider,
} from 'react-admin';
import {
  Chip,
  Box,
  Typography,
  Button,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Person
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { API_CONFIG } from '../../../config/api';
import { tokenManager } from '../../../utils/tokenManager';

const RfidTransactionFilter = (props: any) => {
  const translate = useTranslate();

  return (
    <Filter {...props}>
      <TextInput label={translate('resources.rfid-transactions.filters.q')} source="q" alwaysOn />
      <TextInput label={translate('resources.rfid-transactions.filters.rfid_user_id')} source="rfid_user_id" />
      <TextInput label={translate('resources.rfid-transactions.filters.user_name')} source="user_name" />
      <SelectInput
        label={translate('resources.rfid-transactions.filters.payment_status')}
        source="payment_status"
        choices={[
          { id: 'pending', name: translate('resources.rfid-transactions.paymentStatuses.pending') },
          { id: 'paid', name: translate('resources.rfid-transactions.paymentStatuses.paid') },
          { id: 'failed', name: translate('resources.rfid-transactions.paymentStatuses.failed') },
          { id: 'refunded', name: translate('resources.rfid-transactions.paymentStatuses.refunded') }
        ]}
        emptyValue=""
        emptyText={translate('resources.rfid-transactions.paymentStatuses.all')}
      />
      <DateInput label={translate('resources.rfid-transactions.filters.from_date')} source="from_date" />
      <DateInput label={translate('resources.rfid-transactions.filters.to_date')} source="to_date" />
      <WheelSafeNumberInput label={translate('resources.rfid-transactions.filters.min_amount')} source="min_amount" />
      <WheelSafeNumberInput label={translate('resources.rfid-transactions.filters.max_amount')} source="max_amount" />
    </Filter>
  );
};

const StatusChip = ({ status }: { status: string }) => {
  const translate = useTranslate();

  const getColor = () => {
    switch (status) {
      case 'paid': return 'success';
      case 'pending': return 'warning';
      case 'failed': return 'error';
      case 'refunded': return 'info';
      default: return 'default';
    }
  };

  return (
    <Chip
      label={translate(`resources.rfid-transactions.paymentStatuses.${status}`, { _: status })}
      color={getColor() as any}
      size="small"
    />
  );
};

const PaymentMethodChip = ({ method }: { method: string }) => {
  const translate = useTranslate();

  const getColor = () => {
    switch (method) {
      case 'stripe': return 'primary';
      case 'alipay': return 'info';
      case 'wechat': return 'success';
      case 'manual': return 'warning';
      default: return 'default';
    }
  };

  return (
    <Chip
      label={method ? translate(`resources.rfid-transactions.payment_methods.${method}`, { _: method }) : 'N/A'}
      color={getColor() as any}
      size="small"
      variant="outlined"
    />
  );
};

const DesktopListActions = () => {
  const refresh = useRefresh();
  const notify = useNotify();
  const translate = useTranslate();

  const handleExport = async () => {
    try {
      const token = await tokenManager.getAccessToken();
      const response = await fetch(`${API_CONFIG.baseURL}/api/backend/rfid-transactions/export`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rfid-transactions-${format(new Date(), 'yyyyMMdd-HHmmss')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      notify(translate('resources.rfid-transactions.messages.exportSuccess'), { type: 'success' });
    } catch (error) {
      notify(translate('resources.rfid-transactions.messages.exportFailed'), { type: 'error' });
    }
  };

  return (
    <TopToolbar>
      <Button
        startIcon={<RefreshIcon />}
        onClick={refresh}
        variant="outlined"
        size="small"
        sx={{ mr: 1 }}
      >
        {translate('resources.rfid-transactions.actions.refresh')}
      </Button>
      <Button
        startIcon={<DownloadIcon />}
        onClick={handleExport}
        variant="contained"
        size="small"
      >
        {translate('resources.rfid-transactions.actions.export')}
      </Button>
    </TopToolbar>
  );
};

const MobileListActions = () => {
  const refresh = useRefresh();
  const notify = useNotify();
  const translate = useTranslate();

  const handleExport = async () => {
    try {
      const token = await tokenManager.getAccessToken();
      const response = await fetch(`${API_CONFIG.baseURL}/api/backend/rfid-transactions/export`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rfid-transactions-${format(new Date(), 'yyyyMMdd-HHmmss')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      notify(translate('resources.rfid-transactions.messages.exportSuccess'), { type: 'success' });
    } catch (error) {
      notify(translate('resources.rfid-transactions.messages.exportFailed'), { type: 'error' });
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, p: 0 }}>
      <Button
        startIcon={<RefreshIcon />}
        onClick={refresh}
        size="small"
        sx={{ minWidth: 'auto' }}
      >
        {translate('resources.rfid-transactions.actions.refresh')}
      </Button>
      <Button
        startIcon={<DownloadIcon />}
        onClick={handleExport}
        size="small"
        sx={{ minWidth: 'auto' }}
      >
        {translate('resources.rfid-transactions.actions.export')}
      </Button>
    </Box>
  );
};

const MobileList = () => {
  const { data, isLoading } = useListContext();
  const navigate = useNavigate();
  const translate = useTranslate();
  if (isLoading || !data) return null;
  return (
    <Box>
      {data.map((record: any) => (
        <RecordContextProvider key={record.id} value={record}>
          <Box
            onClick={() => navigate(`/rfid-transactions/${record.id}/show`)}
            sx={{ py: 1.5, px: 2, borderBottom: '1px solid #F0F0F0', cursor: 'pointer' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                  HKD {parseFloat(record.amount || 0).toFixed(2)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {parseFloat(record.energy_delivered || 0).toFixed(2)} kWh
                </Typography>
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <StatusChip status={record.payment_status} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Person sx={{ fontSize: 14, color: '#999' }} />
                <Typography variant="caption" color="text.secondary">
                  {record.rfid_user?.name || 'N/A'}
                </Typography>
              </Box>
              {record.payment_method && (
                <PaymentMethodChip method={record.payment_method} />
              )}
              <Typography variant="caption" color="text.secondary">
                {record.created_at ? new Date(record.created_at).toLocaleDateString() : ''}
              </Typography>
            </Box>
          </Box>
        </RecordContextProvider>
      ))}
    </Box>
  );
};

export const RfidTransactionsList = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <List
      resource="rfid-transactions"
      filters={<RfidTransactionFilter />}
      actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
      empty={isMobile ? false : undefined}
      perPage={25}
      sort={{ field: 'created_at', order: 'DESC' }}
      title={translate('resources.rfid-transactions.name')}
      sx={{
        '& .RaList-content': {
          backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
        },
        ...(isMobile && {
          '& .RaList-main': { margin: 0 },
          '& .RaList-content': { backgroundColor: 'transparent !important', boxShadow: 'none !important', mt: 2 },
          '& .RaList-actions': { backgroundColor: 'transparent !important', p: '0 !important', minHeight: 'auto !important' },
          '& .RaListToolbar-root': { backgroundColor: 'transparent !important', flexDirection: 'row !important', flexWrap: 'wrap !important', alignItems: 'center !important', gap: '8px !important' },
          '& .RaListToolbar-root > span': { display: 'none' },
          '& .RaFilterButton-root, & .add-filter': { display: 'none !important' },
          '& .RaListToolbar-root > form, & .RaFilterForm-root': { flex: '1 1 100% !important', width: '100% !important' },
          '& .filter-field, & .RaFilterForm-filterFormInput': { width: '100% !important' },
          '& .filter-field .MuiFormControl-root': { width: '100% !important' },
          '& .filter-field .MuiInputBase-root': { width: '100% !important' },
          '& .MuiToolbar-root': { backgroundColor: 'transparent !important', minHeight: 'auto !important' },
        }),
      }}
    >
      {isMobile ? (
        <MobileList />
      ) : (
        <Datagrid
          bulkActionButtons={false}
          sx={{
            '& .RaDatagrid-headerCell': {
              backgroundColor: '#F5F5F5',
              fontWeight: 'bold',
            },
            '& .RaDatagrid-row': {
              '&:hover': {
                backgroundColor: '#F0F7FF',
              },
            },
          }}
        >
          <TextField source="id" label={translate('resources.rfid-transactions.fields.id')} />
          <DateField
            source="created_at"
            label={translate('resources.rfid-transactions.labels.dateTime')}
            showTime
          />
          <FunctionField
            label={translate('resources.rfid-transactions.labels.user')}
            render={(record: any) => (
              <Box>
                <Typography variant="body2">{record.rfid_user?.name || 'N/A'}</Typography>
                {record.rfid_user?.email && (
                  <Typography variant="caption" color="textSecondary">
                    {record.rfid_user.email}
                  </Typography>
                )}
              </Box>
            )}
          />
          <FunctionField
            label={translate('resources.rfid-transactions.labels.amount')}
            render={(record: any) => {
              const amount = parseFloat(record.amount || 0);
              return (
                <Typography
                  variant="body2"
                  fontWeight="bold"
                >
                  HKD {amount.toFixed(2)}
                </Typography>
              );
            }}
          />
          <FunctionField
            label={translate('resources.rfid-transactions.labels.energy')}
            render={(record: any) => {
              const energy = parseFloat(record.energy_delivered || 0);
              return (
                <Typography variant="body2">
                  {energy.toFixed(2)} kWh
                </Typography>
              );
            }}
          />
          <FunctionField
            label={translate('resources.rfid-transactions.labels.paymentMethod')}
            render={(record: any) => <PaymentMethodChip method={record.payment_method} />}
          />
          <FunctionField
            label={translate('resources.rfid-transactions.labels.paymentStatus')}
            render={(record: any) => <StatusChip status={record.payment_status} />}
          />
          <ShowButton label="" />
        </Datagrid>
      )}
    </List>
  );
};

export default RfidTransactionsList;
